import React from 'react';
import styled from 'styled-components';
import Select from 'react-select';

const TableWrapper = styled.div`
  width: 100%;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 12px;
`;

const Th = styled.th`
  background: ${({ theme }) => theme.colors.card};
  color: ${({ theme }) => theme.colors.primary};
  padding: 8px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
`;

const Td = styled.td`
  padding: 8px;
  text-align: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 32px 0 0 0;
  gap: 16px;
`;

interface RotationTableProps {
  dates: Date[];
  roles: string[];
  assignments: { [name: string]: string[] };
  unavailable: { [name: string]: Date[] };
  table: { [date: string]: { [role: string]: string } };
  names: string[];
  onChange: (date: string, role: string, name: string) => void;
  randomButton?: React.ReactNode;
}

const RotationTable: React.FC<RotationTableProps> = ({
  dates, roles, assignments, unavailable, table, names, onChange, randomButton
}) => {
  // 날짜 오름차순 정렬
  const sortedDates = [...dates].sort((a, b) => a.getTime() - b.getTime());

  const handleRandomAssign = () => {
    if (roles.length === 0 || names.length === 0) {
      alert('역할과 이름을 모두 입력해 주세요.');
      return;
    }
    const hasRoleAssignment = names.some(name => assignments[name] && assignments[name].length > 0);
    const hasUnavailable = names.some(name => unavailable[name] && unavailable[name].length > 0);
    if (!hasRoleAssignment && !hasUnavailable) {
      alert('역할담당과 로테이션 불가능한 날짜에 최소 한 명 이상 값이 있어야 합니다.');
      return;
    }
    const newTable: { [date: string]: { [role: string]: string } } = {};
    sortedDates.forEach(date => {
      const dateStr = date.toISOString();
      newTable[dateStr] = {};
      // 날짜별로 이미 배정된 사람을 추적
      const assigned: Set<string> = new Set();
      roles.forEach(role => {
        // 후보자: 역할담당에 포함, 불가능날짜에 포함X, 이미 배정되지 않은 사람
        const candidates = names.filter(name =>
          assignments[name]?.includes(role) &&
          !(unavailable[name]?.some(d => d.toDateString() === date.toDateString())) &&
          !assigned.has(name)
        );
        if (candidates.length > 0) {
          const randomIdx = Math.floor(Math.random() * candidates.length);
          const selected = candidates[randomIdx];
          newTable[dateStr][role] = selected;
          assigned.add(selected);
        } else {
          newTable[dateStr][role] = '';
        }
      });
    });
    // setRotationTable(newTable);
  };

  return (
    <TableWrapper>
      <Table>
        <thead>
          <tr>
            <Th>로테이션 배정</Th>
            {sortedDates.map(date => (
              <Th key={date.toISOString()}>
                {date.toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit', weekday: 'short' })}
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {roles.map(role => (
            <tr key={role}>
              <Td>{role}</Td>
              {sortedDates.map(date => {
                const dateStr = date.toISOString();
                // 이미 이 날짜에 배정된 사람들
                const alreadyAssigned = Object.values(table[dateStr] || {}).filter(Boolean);
                // 후보자: 역할담당, 불가능날짜X, 이미 배정X
                const candidates = names.filter(name =>
                  assignments[name]?.includes(role) &&
                  !(unavailable[name]?.some(d => d.toDateString() === date.toDateString())) &&
                  (!alreadyAssigned.includes(name) || table[dateStr]?.[role] === name) // 현재 셀의 값은 허용
                );
                return (
                  <Td key={dateStr}>
                    <Select
                      options={candidates.map(n => ({ value: n, label: n }))}
                      value={table[dateStr]?.[role] ? { value: table[dateStr][role], label: table[dateStr][role] } : null}
                      onChange={selected => onChange(dateStr, role, selected?.value || '')}
                      isClearable
                      placeholder="선택"
                      styles={{
                        container: base => ({ ...base, minWidth: 80 }),
                        control: base => ({ ...base, background: '#232323', borderColor: '#333', color: '#fff', minHeight: 32 }),
                        menu: base => ({ ...base, background: '#232323', color: '#fff' }),
                        singleValue: base => ({ ...base, color: '#fff' }),
                      }}
                    />
                  </Td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </Table>
      {randomButton && <ButtonWrapper>{randomButton}</ButtonWrapper>}
    </TableWrapper>
  );
};

export default RotationTable; 